import z from "zod";

export const GLOBAL_ADMIN_PERMISSION = "_global_:admin" as const;

export const permissionSuffixes = Object.freeze([
  "order:read",
  "order:edit",
  "quote:read",
  "quote:edit",
  "customer:invite",
  "customer:edit",
] as const);

export const permissionSuffix = z.enum(permissionSuffixes);
export type PermissionSuffix = z.output<typeof permissionSuffix>;
export type Permission =
  | `${string}:${PermissionSuffix}`
  | typeof GLOBAL_ADMIN_PERMISSION;

export function hasGlobalAdminPermission(permissions: Permission[]) {
  return hasPermission(permissions, GLOBAL_ADMIN_PERMISSION);
}
export function hasPermission(
  permissions: Permission[],
  perm: Permission,
): boolean {
  return permissions.includes(perm);
}

export function hasAnyPermissionWithSuffix(
  permissions: Permission[],
  suffix: PermissionSuffix,
): boolean {
  return permissions.some((perm) => {
    return splitPermission(perm)?.[1] === suffix;
  });
}

export const permissionsByAccount = z.record(
  z.string(),
  z.array(permissionSuffix),
);
export type PermissionsByAccount = z.output<typeof permissionsByAccount>;
export function groupPermissionsByAccountKey(permissions: Permission[]) {
  const grouped: PermissionsByAccount = {};

  for (const permission of permissions) {
    if (permission === GLOBAL_ADMIN_PERMISSION) continue;
    const split = splitPermission(permission);
    if (!split) continue;
    const [accountKey, permissionSuffix] = split;
    if (!grouped[accountKey]) {
      grouped[accountKey] = [];
    }
    grouped[accountKey].push(permissionSuffix);
  }
  return grouped;
}
/**
 * Splits a permission into its account key and suffix.
 */
export function splitPermission(
  permission: Permission,
): [string, PermissionSuffix] | null {
  if (permission === GLOBAL_ADMIN_PERMISSION) return null;

  const [accountKey, ...rest] = permission.split(":");
  const permissionSuffix = rest.join(":") as PermissionSuffix;
  if (!accountKey || !permissionSuffixes.includes(permissionSuffix))
    return null;
  return [accountKey, permissionSuffix] as const;
}

export function flattenPermissionsByAccount(
  permissionsByAccount: PermissionsByAccount,
): Permission[] {
  return Object.entries(permissionsByAccount).flatMap(
    ([accountKey, accountPermissionSuffixes]) =>
      accountPermissionSuffixes.map<Permission>(
        (suffix) => `${accountKey}:${suffix}`,
      ),
  );
}

export function isPermission(perm: string): perm is Permission {
  if (perm === GLOBAL_ADMIN_PERMISSION) return true;

  const idx = perm.indexOf(":");
  if (idx === -1) return false;

  const accountKey = perm.slice(0, idx);
  const hasValidAccount = accountKey.toLocaleUpperCase() === accountKey; // no lower-case allowed
  const suffix = perm.slice(idx + 1);
  const hasValidSuffix = (permissionSuffixes as readonly string[]).includes(
    suffix,
  );
  return hasValidAccount && hasValidSuffix;
}

export function getAccountsFromPermissions(permissions: Permission[]) {
  return Object.keys(groupPermissionsByAccountKey(permissions));
}

export function getAccountsWithPermission(
  permissions: Permission[],
  type: PermissionSuffix,
) {
  const accounts = new Set<string>();
  for (const permission of permissions) {
    if (permission.endsWith(type)) {
      const accountKey = permission.split(":")[0];
      if (accountKey) accounts.add(accountKey);
    }
  }
  return [...accounts];
}

export function getFullPermissionsForAccount(accountId: string): Permission[] {
  return permissionSuffixes.map(
    (suffix) => `${accountId}:${suffix}` as Permission,
  );
}

export const permissionSuffixLabels = {
  "order:read": "View Orders",
  "order:edit": "Submit Orders",
  "quote:read": "View Quotes",
  "quote:edit": "Create/Edit Quotes",
  "customer:invite": "Invite Users",
  "customer:edit": "Edit Account Info",
} as const satisfies Record<PermissionSuffix, string>;
